import { ReactNode } from 'react';
import { Link } from 'wouter';
import { useMixpanelTrackLink } from '../analytics/mixpanel';

/** A wrapper for url navigation that could be either
 * 1. Internal navigation between pages of this React SPA,
 * 2. External navigation to a different website, or
 * 3. Navigation to the legacy app
 *
 * Let's avoid using this when possible; this is meant to be a convenience component for links that are currently
 * pointing for the old site or externally, but are hopefully going to be brought into the SPA soon. Using a
 * specific <a> or <Link> tag is preferred when possible.
 */
const FlexibleLink = ({
  href,
  className,
  type = 'internal',
  newTab,
  eventName,
  children,
}: {
  /** The url for navigation. Should be a relative url for internal and legacy nav, and an absolute url for external
   *
   * Relative hrefs should begin with a '/'
   **/
  href: string;
  className?: string;
  type: 'internal' | 'external' | 'legacy';
  /** Whether the link opens in a new tab, overriding default behavior. By default, 'external' links open in new tabs */
  newTab?: boolean;
  /** The name of a Mixpanel event fired when this link is clicked */
  eventName?: string;
  children: ReactNode;
}) => {
  const trackLinkRef = useMixpanelTrackLink<HTMLAnchorElement>(eventName ?? '');
  const target = newTab || (newTab === undefined && type === 'external') ? '_blank' : '_self';
  const adjustedHref =
    type === 'legacy' ? `${import.meta.env['VITE_LEGACY_APP_URL']}${href}` : href;
  const anchorTag = (
    <a
      ref={trackLinkRef}
      className={className}
      href={adjustedHref}
      target={target}
      rel={type === 'external' ? 'noreferrer' : undefined}
    >
      {children}
    </a>
  );

  return (
    <>
      {type === 'internal' ?
        <Link to={href} asChild>
          {anchorTag}
        </Link>
      : anchorTag}
    </>
  );
};

export default FlexibleLink;
